import { Injectable } from "@angular/core";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { ModalController } from "@ionic/angular";
import { MmUpdateAppComponent } from "../../components/mm-update-app/mm-update-app.component";
import { FlagsService } from "../config/flags.service";
import { MessageService } from "../message-service/message.service";

export interface OtaConfig {
    enabled: boolean;
    version: string;
    url: string;
    checksum: string;
    force_update: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class UpdateAppOtaService {

    private isUpdating = false;

    constructor(private flagsService: FlagsService,
        private messageService: MessageService,
        private modalCtrl: ModalController
    ) { }

    /**
     * Checks update app
     * @author Starley Cazorla
     */
    async checkUpdate() {

        if (Capacitor.getPlatform() === 'web') {
            return;
        }

        // Evita chamadas concorrentes ou re-entrada após reload
        if (this.isUpdating) {
            console.log('⏳ Update já em andamento, ignorando');
            return;
        }

        try {
            let config = this.flagsService.ota_live_app;
            if (!config.enabled || !config.url || !config.version || !config.checksum) {
                console.log('⛔ OTA desabilitado ou sem configuração');
                return;
            }

            const current = await CapacitorUpdater.current();
            const currentVersion = current?.bundle?.version;

            if (this.isSameOrNewerVersion(currentVersion, config.version)) {
                return;
            }

            const { bundles } = await CapacitorUpdater.list();
            const alreadyInstalled = bundles.some(
                (b) => b.version === config.version && b.status === 'success'
            );
            if (alreadyInstalled) {
                return;
            }

            if (config.force_update) {
                await this.applyUpdateApp(config, true);
            } else {
                await this.showModalUpdateApp(config);
            }

        } catch (error) {
            this.isUpdating = false;
            console.log(error);
        }

    }

    /**
     * Compares versions to check if current is same or newer than target
     * @author Starley Cazorla
     */
    private isSameOrNewerVersion(current: string, target: string): boolean {
        if (!current || !target) return false;
        if (current === target) return true;
        if (current === 'builtin') return false;

        const currentParts = current.split('.').map(Number);
        const targetParts = target.split('.').map(Number);

        for (let i = 0; i < Math.max(currentParts.length, targetParts.length); i++) {
            const c = currentParts[i] || 0;
            const t = targetParts[i] || 0;
            if (c > t) return true;
            if (c < t) return false;
        }
        return true;
    }

    /**
     * Shows modal update app
     * @author Starley Cazorla
     */
    async showModalUpdateApp(config: OtaConfig) {
        const modal = await this.modalCtrl.create({
            component: MmUpdateAppComponent,
            cssClass: 'modalInterno',
            backdropDismiss: false,
            componentProps: {
                fromOta: true
            }
        });
        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.update) {
            await this.applyUpdateApp(config, true);
        }

    }

    /**
     * Applies update app
     * @author Starley Cazorla
     * @param config
     * @param [silent]
     */
    async applyUpdateApp(config: OtaConfig, silent: boolean = false) {
        this.isUpdating = true;

        if (silent) {
            await this.messageService.startLoading('Baixando atualização...', 60000);
        }

        try {
            const bundle = await CapacitorUpdater.download({
                url: config.url,
                version: config.version,
                checksum: config.checksum
            });

            if (bundle.status === 'error') {
                throw new Error('Bundle download failed');
            }

            await CapacitorUpdater.set(bundle);
            await this.messageService.endLoading();
            await CapacitorUpdater.reload();

        } catch (error) {
            this.isUpdating = false;
            await this.messageService.endLoading();
            console.log('Error applying update:', error);
            await this.messageService.showToast('Erro ao baixar atualização! Tentaremos novamente mais tarde.', 'danger', 'bottom', 5000, false);
        }

    }

    async getCurrentBundleVersion() {
        if (Capacitor.getPlatform() === 'web') {
            return '';
        }
        const current = await CapacitorUpdater.current();
        const appVersion = await App.getInfo();
        if (current?.bundle?.version === appVersion.version) {
            return '';
        }
        return ' - ' + (current?.bundle?.version || '');
    }

}
